import { PATH_DICTS_PAYROLL_TYPES } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType, mergeFilters } from "@/lib/filters";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";
import { PayrollTypesService } from "@/services/payrollType";
import { ReadPayrollType } from "@/types/payrollType";
import { ProjectDependsOn } from "@/schemas/commonFields/projectDependsOn";

interface PayrollTypeSelectProps extends AsyncSelectBaseProps {
  className?: string;
  projectDependsOn?: ProjectDependsOn;
  defaultItem?: Partial<Option>;
  allowClearSelection?: boolean;
}

export default forwardRef<HTMLButtonElement, PayrollTypeSelectProps>(
  function PayrollTypeSelect(
    { value, projectDependsOn, defaultItem, allowClearSelection, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const options = useCreateDataTableOptions("payrollTypeList");

    const queryKey = useDynamicQueryParams<ReadPayrollType>(
      "payrollTypeList",
      options,
      projectDependsOn ? { projectDependsOn } : undefined,
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onFilter"] =
      useCallback(
        (filterValue: string) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    const onChange: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (e, selectedItem) => {
          props.onChange(e, selectedItem);
        },
        [props],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            PayrollTypesService.getList({
              filters: projectDependsOn
                ? mergeFilters(options.filters, [
                    {
                      id: "project_depends_on",
                      value: {
                        value: projectDependsOn,
                        filter: FilterType.EqualsString,
                      },
                    },
                  ])
                : options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("payrollTypes.select.placeholder")}
          loadingLabel={t("payrollTypes.select.loading")}
          noResultsLabel={t("payrollTypes.select.noResults")}
          searchLabel={t("payrollTypes.select.search")}
          enableFilter
          allowClearSelection={allowClearSelection}
          {...props}
          onFilter={onFilter}
          onChange={onChange}
          value={value ? value.toString() : undefined}
          defaultItem={defaultItem}
          ref={ref}
          renderName={(item: ReadPayrollType) => <div>{item.name}</div>}
        />
        <EntityListLink
          path={PATH_DICTS_PAYROLL_TYPES}
          label={t("payrollTypes.select.link")}
        />
      </span>
    );
  },
);
